import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { AppDispatch } from 'config/redux'
import { isCommentsCounterRequestPending, scheduleCommentsCounterFetch } from './commentsCounterQueue'

export const useContextCommentsCounter = (oids: string[]) => {
  const dispatch: AppDispatch = useDispatch()
  const commentsCounter = useSelector((state: RootState) => state.comments.commentsCounter)

  const [loading, setLoading] = useState(false)
  const [queueVersion, setQueueVersion] = useState(0)

  const commentsCounterRef = useRef(commentsCounter)

  const oidsKey = useMemo(() => oids.join('|'), [oids])

  commentsCounterRef.current = commentsCounter

  const totalCommentsCount = useMemo(() => {
    const currentOids = oidsKey ? oidsKey.split('|') : []

    return currentOids.reduce((total, oid) => {
      const count = commentsCounter[oid] || 0

      return total + count
    }, 0)
  }, [commentsCounter, oidsKey])

  useEffect(() => {
    const oidsToFetch = oidsKey ? oidsKey.split('|').filter((oid) => !(oid in commentsCounterRef.current)) : []

    if (oidsToFetch.length === 0) {
      setLoading(false)
      return
    }

    setLoading(true)

    const unsubscribeRequests = oidsToFetch.map((oid) =>
      scheduleCommentsCounterFetch(oid, dispatch, () => setQueueVersion((version) => version + 1))
    )

    return () => {
      unsubscribeRequests.forEach((unsubscribe) => unsubscribe())
    }
  }, [dispatch, oidsKey])

  useEffect(() => {
    const currentOids = oidsKey ? oidsKey.split('|') : []
    const hasPendingCounters = currentOids.some(
      (oid) => !(oid in commentsCounter) && isCommentsCounterRequestPending(oid)
    )

    setLoading(hasPendingCounters)
  }, [commentsCounter, oidsKey, queueVersion])

  return { totalCommentsCount, loading }
}
